<template>
  <DialogFull
    :data="dialogSettings"
    @close="onClose"
    progress
    :steps="steps"
    @onStepBack="stepBack"
  >
    <template v-slot:dialog.body>
      <div class="container col-md-8 col-sm-12">
        <div v-if="isFetching">
          <v-skeleton-loader type="list-item-three-line" />
        </div>
        <div v-else>
          <div v-for="(step, index) in allSteps" :key="index">
            <component
              :ref="step.ref"
              :is="step.component"
              @nextStep="nextStep"
              :userId="self.id"
              v-if="
                shouldStepViewBeShown(index) ||
                  ifStepHasAlreadyBeenCompleted(index)
              "
              v-show="shouldStepViewBeShown(index)"
              :isEditMode="isEditMode"
            />
          </div>
        </div>
      </div>
    </template>
  </DialogFull>
</template>

<script>
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { USERS_NAMESPACE } from "@/store/modules/users";
import { createNamespacedHelpers } from "vuex";
import ContractTypes from "@/components/profile/steps/ContractTypes";
import WorkingHours from "@/components/profile/steps/WorkingHours";
import JobFunction from "@/components/profile/steps/JobFunction";
import Location from "@/components/profile/steps/Location";
import Salary from "@/components/profile/steps/Salary";
import DialogFull from "@/components/common/DialogFull";
import { FETCH_CURRENT_USER } from "@/store/modules/users/actions";
import { PERMISSIONS } from "@/constants/workers";
import { isPermissioned } from "@/utils/permissions";
import { size, map, first, isEmpty } from "lodash";
import { DATA_NAMESPACE } from "@/store/modules/data";
import {
  FETCH_WORKING_HOURS_GROUPINGS,
  FETCH_CONTRACT_TYPES_GROUPINGS,
  FETCH_LOCATION_GROUPINGS
} from "@/store/modules/data/actions";
import {
  GET_WORKING_HOURS_GROUPINGS,
  GET_CONTRACT_TYPES_GROUPINGS,
  GET_LOCATION_GROUPINGS
} from "@/store/modules/data/getters";
import { FETCH_EXPERIENCE_FUNCTIONS } from "@/store/modules/users/experience/actions";
import { EXPERIENCE_NAMESPACE } from "@/store/modules/users/experience";
import { copyState } from "@/utils/helpers";

const { PREFERENCES: PREFERENCES_PERMISSIONS } = PERMISSIONS;
const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);
const { mapActions: mapUserActions } = createNamespacedHelpers(USERS_NAMESPACE);
const { mapGetters, mapActions } = createNamespacedHelpers(DATA_NAMESPACE);
const { mapActions: mapExperienceActions, mapState } = createNamespacedHelpers(
  EXPERIENCE_NAMESPACE
);
export default {
  name: "ProfilePreferences",
  components: {
    DialogFull
  },
  props: {
    isOpen: Boolean,
    isEditMode: Boolean
  },
  async created() {
    this.isFetching = true;
    await this.fetchWorkingHours();
    await this.fetchContractTypes();
    await this.fetchLocations();
    await this.fetchExperienceFunctions({
      filter: {
        is_preference: true
      }
    });
    this.isFetching = false;
  },
  data() {
    return {
      steps: { current: 1, saved: 0 },
      isFetching: false
    };
  },
  computed: {
    ...mapGetters({
      contractTypes: GET_CONTRACT_TYPES_GROUPINGS,
      locationsGroupings: GET_LOCATION_GROUPINGS,
      workingHours: GET_WORKING_HOURS_GROUPINGS
    }),
    ...mapState(copyState(["experienceFunctions"])),
    ...mapAuthState({
      self: state => state.userData
    }),
    dialogSettings() {
      return {
        dialog: this.isOpen,
        title: "Profile Preferences"
      };
    },
    totalSteps() {
      return size(this.allSteps);
    },
    allSteps() {
      return [
        this.showStep(PREFERENCES_PERMISSIONS.contractType) &&
          !isEmpty(this.contractTypes) && {
            component: ContractTypes,
            ref: "contractTypes"
          },
        this.showStep(PREFERENCES_PERMISSIONS.workingHours) &&
          !isEmpty(this.workingHours) && {
            component: WorkingHours,
            ref: "workingHours"
          },
        this.showStep(PREFERENCES_PERMISSIONS.jobFunction) &&
          !isEmpty(this.experienceFunctions) && {
            component: JobFunction,
            ref: "jobFunction"
          },
        this.showStep(PREFERENCES_PERMISSIONS.location) &&
          !isEmpty(this.locationsGroupings) && {
            component: Location,
            ref: "location"
          },
        this.showStep(PREFERENCES_PERMISSIONS.salary) && {
          component: Salary,
          ref: "salary"
        }
      ].filter(Boolean);
    }
  },
  methods: {
    ...mapActions({
      fetchContractTypes: FETCH_CONTRACT_TYPES_GROUPINGS,
      fetchWorkingHours: FETCH_WORKING_HOURS_GROUPINGS,
      fetchLocations: FETCH_LOCATION_GROUPINGS
    }),
    ...mapExperienceActions({
      fetchExperienceFunctions: FETCH_EXPERIENCE_FUNCTIONS
    }),
    ...mapUserActions({
      fetchCurrentUser: FETCH_CURRENT_USER
    }),
    onClose() {
      this.fetchCurrentUser();
      this.$emit("close");
    },
    stepBack() {
      if (this.steps.current > 1) {
        this.steps.current -= 1;
      }
    },
    showStep(permission) {
      return isPermissioned(`workers.edit.${permission}`);
    },
    stepForward() {
      this.steps.current += 1;
      if (
        this.steps.saved < this.totalSteps &&
        this.steps.current > this.steps.saved
      ) {
        this.steps.saved += 1;
      }
    },
    async nextStep() {
      if (this.steps.current === this.totalSteps) {
        const refs = map(this.allSteps, "ref");
        const requests = map(refs, ref =>
          first(this.$refs[ref]).handleUpload()
        );
        await Promise.all(requests);
        this.onClose();
      } else {
        this.stepForward();
      }
    },
    ifStepHasAlreadyBeenCompleted(index) {
      const viewStep = index + 1;
      return viewStep <= this.steps.saved;
    },
    shouldStepViewBeShown(index) {
      const viewStep = index + 1;
      return viewStep <= this.totalSteps && this.steps.current === viewStep;
    }
  }
};
</script>

<style lang="scss"></style>
